import React, { useState } from 'react';
import { Loader2, CheckCircle2 } from 'lucide-react';
import HeroPlaceholder from '../components/HeroPlaceholder';
import FaqAccordion from '../components/FaqAccordion';
import CtaBand from '../components/CtaBand';
import SEO from '../components/SEO';
import AeoCallout from '../components/AeoCallout';
import RelatedBand from '../components/RelatedBand';
import { HERO_MEDIA } from '../config/media';
import { submitContact } from '../lib/api';
import { usePageContent } from '../lib/useContent';
import {
    orgJsonLd,
    breadcrumbJsonLd,
    faqJsonLd,
} from '../lib/seo';

const PILLARS = [
    {
        head: 'Volume pricing',
        body: 'Manufacturer Direct pricing on 500 + mat orders. No broker margin between you and the mill.',
    },
    {
        head: 'Custom specifications',
        body: 'Any size, any bolt pattern, any timber grade. Custom-spec orders engineered alongside our team.',
    },
    {
        head: 'Predictable lead times',
        body: 'Mill-schedule transparency from our Chemainus, BC facility. Bring milestones, we\u2019ll align.',
    },
    {
        head: 'Coordinated logistics',
        body: 'Manufacturing capacity plus an established hauling network. Full-load dispatch, BC \u2192 NT.',
    },
];

const VOLUMES = [
    { value: '', label: 'Typical annual volume' },
    { value: '<500', label: 'Less than 500 mats / year' },
    { value: '500-2000', label: '500 - 2,000 mats / year' },
    { value: '2000-10000', label: '2,000 - 10,000 mats / year' },
    { value: '10000+', label: '10,000 + mats / year' },
];

const REGIONS = ['BC', 'AB', 'SK', 'MB', 'YT', 'NT', 'Other'];

// Frozen inline fallback - matches current page JSX verbatim.
const PROC_FALLBACK = {
    hero: {
        eyebrow: 'Procurement Partnerships',
        headline: 'Manufacturer Direct procurement for Tier 1 contractors and matting service companies.',
        body: 'High-volume access, rig, and crane mats - built at our Chemainus, BC mill from coastal Hem-Fir Hi-Line #2 stock. No broker margin, predictable lead times, full custom-spec capability.',
        image_url: HERO_MEDIA.logisticsDepot.src,
    },
    body: {
        lead: '',
        paragraphs: [
            'Centurion Matting Products is a wholesaler as well as a manufacturer. We supply matting service companies, EPCs, owner-operators, and First Nations partner organizations with high-volume access, rig, and crane mats - built at our Chemainus, BC mill from coastal Hem-Fir Hi-Line #2 stock, with sales coordination from our Calgary, AB office.',
            'Manufacturer Direct means no broker margin, predictable lead times tied to our mill schedules, and full custom-spec capability. Procurement teams get a direct line to the same engineering and operations leadership who built Centurion\u2019s reputation since 1955.',
        ],
    },
    faq: {
        items: [
            {
                q: 'What\u2019s the minimum order Centurion accepts?',
                a: 'There is no rigid minimum - we supply small top-up orders through to multi-thousand-mat infrastructure programs. For wholesale / reseller pricing the typical entry point is in the hundreds of mats per order.',
            },
            {
                q: 'Does Centurion offer credit terms?',
                a: 'Standard Net 30 terms are available subject to credit approval. New procurement partners typically settle the first order on PO with subsequent orders moving to terms once credit is established.',
            },
            {
                q: 'Can Centurion ship to all Western Canadian provinces?',
                a: 'Yes - British Columbia, Alberta, Saskatchewan, Manitoba, Yukon, Northwest Territories, and beyond are all served Manufacturer Direct from our Chemainus, BC mill, coordinated through our Calgary, AB sales office. Out-of-region (Eastern Canada, US) deliveries quoted on request.',
            },
            {
                q: 'How are fuel surcharges handled on long-haul deliveries?',
                a: 'The quoted fuel surcharge is based on current fuel and freight market conditions. In the event of material fluctuations prior to or during delivery, Centurion reserves the right to adjust the fuel surcharge with advance notice and supporting market data - the same logic that governs our public estimate disclaimer.',
            },
        ],
    },
};

export default function Procurement() {
    const content = usePageContent('partnerships.procurement', PROC_FALLBACK);
    const hero = content.hero || PROC_FALLBACK.hero;
    const body = content.body || PROC_FALLBACK.body;
    const faqs = (content.faq?.items?.length ? content.faq.items : PROC_FALLBACK.faq.items);

    const [form, setForm] = useState({
        name: '',
        company: '',
        email: '',
        phone: '',
        volume: '',
        regions: [],
        message: '',
        website: '',
    });
    const [status, setStatus] = useState('idle');
    const [error, setError] = useState('');

    const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
    const toggleRegion = (r) =>
        setForm((f) => ({
            ...f,
            regions: f.regions.includes(r)
                ? f.regions.filter((x) => x !== r)
                : [...f.regions, r],
        }));

    const onSubmit = async (e) => {
        e.preventDefault();
        setStatus('submitting');
        setError('');
        try {
            const trailer = [
                form.volume ? `Typical volume: ${form.volume}` : '',
                form.regions.length ? `Preferred regions: ${form.regions.join(', ')}` : '',
            ].filter(Boolean);
            const notes = form.message?.trim() || '';
            // Preserve the blank-line spacer between user notes and the trailer
            // so the admin view reads as two distinct blocks.
            const message = trailer.length
                ? (notes ? `${notes}\n\n${trailer.join('\n')}` : trailer.join('\n'))
                : notes;
            const payload = {
                name: form.name,
                company: form.company || undefined,
                email: form.email,
                phone: form.phone || undefined,
                topic: 'partnership',
                message,
                website: form.website,
            };
            Object.keys(payload).forEach((k) => {
                if (payload[k] === undefined) delete payload[k];
            });
            await submitContact(payload);
            setStatus('success');
        } catch (err) {
            setStatus('error');
            setError(
                err?.response?.data?.detail
                    ? typeof err.response.data.detail === 'string'
                        ? err.response.data.detail
                        : 'Please check the highlighted fields.'
                    : 'Something went wrong. Please try again.'
            );
        }
    };

    return (
        <>
            <SEO
                title="Procurement Partnerships - Manufacturer Direct Wholesale"
                description="Centurion Matting Products supplies Tier 1 contractors, matting service companies, and First Nations partner organizations with manufacturer-direct timber matting. Volume pricing, custom specs, predictable lead times."
                cmsSlug="seo.partnerships.procurement"
                path="/partnerships/procurement"
                image={HERO_MEDIA.logisticsDepot.src}
                jsonLd={[
                    orgJsonLd(),
                    breadcrumbJsonLd([
                        { name: 'Home', path: '/' },
                        { name: 'Partnerships', path: '/partnerships' },
                        { name: 'Procurement', path: '/partnerships/procurement' },
                    ]),
                    faqJsonLd(faqs),
                    {
                        '@context': 'https://schema.org',
                        '@type': 'Service',
                        serviceType: 'Wholesale & Procurement Partnership',
                        provider: { '@type': 'Organization', name: 'Centurion Matting Products' },
                        areaServed: 'Western Canada',
                        url: 'https://centurionmatting.com/partnerships/procurement',
                    },
                ]}
            />

            <HeroPlaceholder
                mediaKey="logisticsDepot"
                imageUrl={hero.image_url}
                eyebrow={hero.eyebrow}
                title={hero.headline}
                subtitle={hero.body}
            />

            <section className="mx-auto max-w-4xl px-6 py-20 md:py-28">
                <span className="eyebrow">Overview</span>
                {(body.paragraphs || []).map((p, i) => (
                    <p
                        key={i}
                        className={i === 0
                            ? 'mt-8 font-sans text-lg leading-relaxed text-brand-text'
                            : 'mt-6 font-sans text-base leading-relaxed text-brand-muted'}
                    >
                        {p}
                    </p>
                ))}
            </section>

            {/* Pillars */}
            <section className="border-y border-brand-hairline bg-brand-surface" data-eyebrow-tone="blue">
                <div className="mx-auto max-w-7xl px-6 py-20 md:px-10 md:py-24">
                    <span className="eyebrow">What Procurement Teams Get</span>
                    <div className="mt-10 grid gap-px bg-brand-hairline md:grid-cols-2 lg:grid-cols-4" data-testid="proc-pillars">
                        {PILLARS.map((p, i) => (
                            <div key={p.head} className="bg-brand-surface p-6 md:p-8">
                                <span className="font-display text-xs font-bold tabular-nums tracking-[0.2em] text-brand-gold">
                                    0{i + 1}
                                </span>
                                <h3 className="mt-5 font-display text-base font-bold uppercase tracking-tight text-brand-text">
                                    {p.head}
                                </h3>
                                <p className="mt-3 font-sans text-sm leading-relaxed text-brand-muted">
                                    {p.body}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Spec callout + form */}
            <section className="mx-auto max-w-7xl grid gap-12 px-6 py-20 md:grid-cols-[1fr_1.4fr] md:px-10 md:py-28 md:gap-16">
                <div>
                    <AeoCallout
                        testId="aeo-proc"
                        q="How do I become a Centurion wholesale partner?"
                        a="Contact our procurement team via the form below or call +1 403-777-2600. We'll review your typical volumes, mat-spec needs, delivery regions, and credit terms, and respond within one business day."
                    />
                    <p className="mt-8 font-sans text-sm leading-relaxed text-brand-muted">
                        Calling? Ask for the procurement desk. We’ll route you to the same engineering and operations team that builds for major Western Canadian projects.
                    </p>
                </div>

                <div>
                    <span className="eyebrow">Procurement Inquiry</span>
                    <h2 className="h-display mt-6 text-2xl sm:text-3xl">Tell us about your supply needs.</h2>

                    {status === 'success' ? (
                        <div
                            data-testid="proc-success"
                            className="mt-10 flex items-start gap-4 border border-brand-gold bg-brand-surface p-6"
                        >
                            <CheckCircle2 className="mt-1 flex-none text-brand-gold" size={20} />
                            <div>
                                <p className="font-display text-base font-bold uppercase tracking-tight text-brand-text">
                                    Inquiry received.
                                </p>
                                <p className="mt-2 font-sans text-sm text-brand-muted">
                                    A Centurion procurement lead will reply within 1 business day.
                                </p>
                            </div>
                        </div>
                    ) : (
                        <form
                            onSubmit={onSubmit}
                            data-testid="proc-form"
                            className="mt-10 grid gap-5"
                            noValidate
                        >
                            <input
                                type="text"
                                name="website"
                                tabIndex={-1}
                                autoComplete="off"
                                value={form.website}
                                onChange={update('website')}
                                style={{ position: 'absolute', left: '-10000px', width: '1px', height: '1px' }}
                                aria-hidden="true"
                            />

                            <div className="grid gap-5 md:grid-cols-2">
                                <Field label="Company" required>
                                    <Input value={form.company} onChange={update('company')} required testId="proc-company" />
                                </Field>
                                <Field label="Name" required>
                                    <Input value={form.name} onChange={update('name')} required testId="proc-name" />
                                </Field>
                            </div>
                            <div className="grid gap-5 md:grid-cols-2">
                                <Field label="Email" required>
                                    <Input type="email" value={form.email} onChange={update('email')} required testId="proc-email" />
                                </Field>
                                <Field label="Phone">
                                    <Input type="tel" value={form.phone} onChange={update('phone')} testId="proc-phone" />
                                </Field>
                            </div>

                            <Field label="Typical annual volume">
                                <select
                                    data-testid="proc-volume"
                                    value={form.volume}
                                    onChange={update('volume')}
                                    className="w-full border border-brand-hairline bg-brand-elevated px-4 py-3 font-sans text-base text-brand-text outline-none transition-colors focus:border-brand-gold"
                                >
                                    {VOLUMES.map((v) => (
                                        <option key={v.value} value={v.value}>
                                            {v.label}
                                        </option>
                                    ))}
                                </select>
                            </Field>

                            <Field label="Preferred delivery regions">
                                <div className="flex flex-wrap gap-2" data-testid="proc-regions">
                                    {REGIONS.map((r) => {
                                        const active = form.regions.includes(r);
                                        return (
                                            <button
                                                type="button"
                                                key={r}
                                                onClick={() => toggleRegion(r)}
                                                data-testid={`proc-region-${r.toLowerCase()}`}
                                                className={[
                                                    'border px-4 py-2 font-display text-[11px] font-bold uppercase tracking-[0.16em] transition-colors',
                                                    active
                                                        ? 'border-brand-gold bg-brand-gold text-brand-base'
                                                        : 'border-brand-hairline bg-brand-elevated text-brand-text hover:border-brand-gold',
                                                ].join(' ')}
                                            >
                                                {r}
                                            </button>
                                        );
                                    })}
                                </div>
                            </Field>

                            <Field label="Notes">
                                <textarea
                                    data-testid="proc-message"
                                    rows={4}
                                    value={form.message}
                                    onChange={update('message')}
                                    placeholder="Spec sketches, credit-terms, project pipeline, or anything else."
                                    className="w-full border border-brand-hairline bg-brand-elevated px-4 py-3 font-sans text-base text-brand-text outline-none transition-colors focus:border-brand-gold"
                                />
                            </Field>

                            {error ? (
                                <p className="font-sans text-sm text-brand-danger" data-testid="proc-error">
                                    {error}
                                </p>
                            ) : null}

                            <button
                                type="submit"
                                data-testid="proc-submit"
                                disabled={status === 'submitting'}
                                className="mt-2 inline-flex items-center justify-center gap-2 bg-brand-gold px-6 py-4 font-display text-xs font-bold uppercase tracking-[0.16em] text-brand-base transition-colors duration-150 hover:bg-brand-gold-hover disabled:opacity-60"
                            >
                                {status === 'submitting' ? (
                                    <>
                                        <Loader2 className="animate-spin" size={14} /> Sending…
                                    </>
                                ) : (
                                    'Send Procurement Inquiry'
                                )}
                            </button>
                        </form>
                    )}
                </div>
            </section>

            {/* FAQ */}
            <section className="border-t border-brand-hairline bg-brand-surface">
                <div className="mx-auto max-w-4xl px-6 py-20 md:py-28">
                    <span className="eyebrow">FAQ</span>
                    <h2 className="h-display mt-6 text-3xl sm:text-4xl">
                        Common procurement questions.
                    </h2>
                    <div className="mt-10">
                        <FaqAccordion faqs={faqs} testId="faq-proc" />
                    </div>
                </div>
            </section>

            <RelatedBand
                heading="Adjacent partnerships."
                items={[
                    { to: '/partnerships/indigenous', eyebrow: 'Indigenous', title: 'Indigenous Partnerships', body: 'First Nations employment, procurement and joint-venture frameworks through our partner, Bear Valley Solutions.' },
                    { to: '/partnerships/sustainability', eyebrow: 'Sustainability', title: 'Sustainability', body: 'Renewable timber, reduced ground impact, multi-deployment lifecycle.' },
                    { to: '/catalog', eyebrow: 'Catalog', title: 'All Products', body: 'The full Centurion product catalog - standard and built-to-spec.' },
                ]}
            />

            <CtaBand
                eyebrow="Procurement"
                title="Source matting Manufacturer Direct."
                body="Talk to procurement about volumes, custom specs, lead times, and credit terms."
                cta={{ to: '/contact?topic=partnership', label: 'Talk to Procurement' }}
                secondary={{ to: '/quote', label: 'Get an Estimated Quote' }}
            />
        </>
    );
}

const Field = ({ label, required, children }) => (
    <label className="block">
        <span className="font-display text-[10px] font-bold uppercase tracking-[0.2em] text-brand-muted">
            {label}
            {required ? <span className="text-brand-gold"> *</span> : null}
        </span>
        <div className="mt-2">{children}</div>
    </label>
);

const Input = ({ testId, ...props }) => (
    <input
        data-testid={testId}
        {...props}
        className="w-full border border-brand-hairline bg-brand-elevated px-4 py-3 font-sans text-base text-brand-text outline-none transition-colors focus:border-brand-gold"
    />
);
